/* eslint-disable parog-web/migrate-non-patch-forms */
import { service } from "@ember/service";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import type Patient from "parog-web/models/patient";
import type ParogService from "parog-web/services/parog";
import type { ForcBaseArgs } from "./base-component";

export interface CrudArgs extends ForcBaseArgs {
  patient: Patient;
  initialModel?: CrudJournalEventBaseComponent["initialModel"];
}

export default class CrudJournalEventBaseComponent<
  T = unknown,
  Args extends CrudArgs = CrudArgs
> extends JournalEventBasedComponent<Args> {
  @service declare parog: ParogService;

  get initialModel(): Record<string, any> {
    return this.args.initialModel ? this.args.initialModel : {};
  }

  override get model(): typeof this.journalEventClass {
    if (this.isNewMode) {
      return this.store.createRecord(this.journalEventClassType, {
        occurredAt: new Date(),
        ...this.initialModel
      });
    }

    return this.retrievedJournalEvent as typeof this.journalEventClass;
  }

  /**
   * Use
   * <WrForm @data={{this.typedChangeset}}>
   * instead of
   * <WrForm @data={{this.changeset}}>
   * to get full typing support in your form
   *
   * See https://github.com/CrowdStrike/ember-headless-form/issues/211
   */
  get typedChangeset(): T {
    return this.changeset as unknown as T;
  }

  async beforeSave(): Promise<void> {
    return;
  }

  /**
   * Bound to the instance, so that a template can pass it as a callback.
   * Subclasses override `save`, which must stay a method to call `super.save()`.
   */
  onSave = () => this.save();

  override async save(): Promise<typeof this.journalEventClass> {
    if (!this.isDirty) {
      return;
    }

    /**
     * Set the patient before saving the changeset, if the record
     * is new.
     * As it is done in the `computePayload` method.
     */
    if (this.isNewMode) {
      this.changeset.set("patient", this.args.patient);
    }

    await this.changeset.execute();
    await this.beforeSave();
    await this.changeset.save();
    await this.draft.onDidSave();

    /**
     * Unlike Forc::Provdider, WrForm is not responsible of any logic after submitting the form
     * under Wiser, this logic now belongs to this save method
     */
    if (this.parog.isWiserEnabled && this.args.afterCompleted)
      this.args.afterCompleted();

    super.popToast();

    return this.changeset.data;
  }
}
